// One place that turns any thrown thing into a sentence a voter can read.
// Ports lib/core/utils/error_localizer.dart: the backend and the Firebase SDK
// both speak English-only strings from layers that have no locale, so the last
// hop before render substring-matches the known ones onto localized keys and
// lets anything else through verbatim.
//
// The web has typed error codes the Flutter app didn't, so an ApiError whose
// code is unambiguous is resolved from the code — the wording of a backend
// message can drift, an enum can't.

import { ApiError, type ApiErrorCode } from "@/types";

type Translate = (key: string, vars?: Record<string, string | number>) => string;

// Codes that pin down exactly one message, checked BEFORE the message patterns.
// `auth`, `not_registered`, `validation` and `server` are deliberately absent:
// api.ts funnels whole status classes into them, so their real meaning only
// lives in the message.
const CODE_KEY: Partial<Record<ApiErrorCode, string>> = {
  already_voted: "alreadyVotedMessage",
  election_not_active: "electionNotActive",
  invalid_candidate_count: "invalidCandidateCount",
  duplicate_candidates: "duplicateCandidatesSelected",
  // `network` is deliberately NOT here: api.ts raises it for BOTH a dead
  // connection and a timeout, and only the message tells them apart. Resolving
  // it by code would collapse "Connection timed out" (retry) into "you are
  // offline" (check your signal). The message rules below split them.
};

// Firebase phone-auth codes ("auth/invalid-verification-code"). Substring match,
// so the "auth/" prefix and any vendor suffix don't matter.
const FIREBASE_KEYS: [needle: string, key: string][] = [
  ["invalid-phone-number", "invalidPhoneFormat"],
  ["too-many-requests", "tooManyAttempts"],
  ["quota-exceeded", "smsQuotaExceeded"],
  ["captcha-check-failed", "authError"],
  ["invalid-app-credential", "authError"],
  ["invalid-verification-code", "invalidOtp"],
  ["code-expired", "verificationExpired"],
  ["session-expired", "verificationExpired"],
  // Not in the Flutter map: the web SDK reports a dropped connection this way,
  // and "offline" is the single most likely failure on a polling-station phone.
  ["network-request-failed", "noInternetConnection"],
];

const all =
  (...needles: string[]) =>
  (m: string) =>
    needles.every((n) => m.includes(n));

const some =
  (...needles: string[]) =>
  (m: string) =>
    needles.some((n) => m.includes(n));

// ORDER IS LOAD-BEARING and matches error_localizer.dart branch for branch:
// several patterns overlap, and the first hit wins (e.g. "failed to load
// election" excludes the plural so the singular can't swallow it).
const MESSAGE_RULES: [test: (m: string) => boolean, key: string][] = [
  // Already voted
  [some("already voted", "has already voted"), "phoneAlreadyVoted"],

  // Phone / verification
  [some("verification id not found"), "verificationIdNotFound"],
  [some("invalid phone", "phone number format"), "invalidPhoneFormat"],
  [some("too many attempts"), "tooManyAttempts"],
  [some("sms quota"), "smsQuotaExceeded"],
  [all("verification", "expired"), "verificationExpired"],
  [(m) => m.includes("invalid") && (m.includes("otp") || m.includes("code")), "invalidOtp"],

  // Authentication
  [some("not registered as voter"), "phoneNotRegisteredAsVoter"],
  [some("invalid firebase token"), "invalidFirebaseToken"],
  [some("session expired"), "sessionExpired"],
  [all("backend auth", "failed"), "backendAuthFailed"],
  [some("no authenticated user"), "noAuthenticatedUser"],
  [some("failed to get id token"), "failedToGetIdToken"],
  [some("failed to sign in"), "failedToSignIn"],
  [some("authentication required"), "authenticationRequired"],
  [some("authentication error"), "authError"],
  [some("access denied"), "accessDenied"],

  // Elections — this one is an exact match, not a substring, in Flutter too.
  [(m) => m === "not_prevalidated", "notPrevalidated"],
  [some("no active election"), "noActiveElectionFound"],
  [all("election", "not active"), "electionNotActive"],
  [all("election id", "required"), "electionIdRequired"],
  [
    (m) => m.includes("failed to load election") && !m.includes("elections"),
    "failedToLoadElection",
  ],
  [some("failed to load elections"), "failedToLoadElections"],
  [all("failed to check", "election"), "failedToCheckElections"],

  // Candidates / voting
  [some("no candidates selected"), "noCandidatesSelected"],
  [some("duplicate candidates"), "duplicateCandidatesDetected"],
  [(m) => m.includes("invalid") && m.includes("candidate"), "invalidCandidateCount"],

  // Network / server
  [some("connection timed out", "timeout"), "connectionTimedOut"],
  [some("no internet", "network error"), "noInternetConnection"],
  [some("server error"), "serverErrorTryLater"],
  [some("validation failed", "validation error"), "validationFailed"],

  // Generic
  [some("request failed"), "requestFailed"],
  [some("unexpected response"), "unexpectedResponse"],
];

// Firebase throws plain objects/FirebaseError, not our ApiError — read the code
// defensively rather than importing the SDK's type into every call site.
function readCode(e: unknown): string {
  const c = (e as { code?: unknown })?.code;
  return typeof c === "string" ? c : "";
}

function readMessage(e: unknown): string {
  if (typeof e === "string") return e;
  const m = (e as { message?: unknown })?.message;
  return typeof m === "string" ? m.trim() : "";
}

/**
 * Best localized sentence for anything thrown: ApiError, Firebase auth error,
 * plain Error, string, or nothing at all.
 *
 * Resolution order:
 *  1. ApiError.code, for the codes that mean exactly one thing.
 *  2. Firebase auth code ("auth/...").
 *  3. Substring match on the message, in ErrorLocalizer's order.
 *  4. The raw message, so a real backend reason is never buried under a
 *     generic string (ErrorLocalizer's `return errorMessage`).
 */
export function localizeError(e: unknown, t: Translate): string {
  if (e === null || e === undefined) return t("unknownError");

  if (e instanceof ApiError) {
    const key = CODE_KEY[e.code];
    if (key) return t(key);
  }

  const firebaseCode = e instanceof ApiError ? "" : readCode(e).toLowerCase();
  if (firebaseCode) {
    const hit = FIREBASE_KEYS.find(([needle]) => firebaseCode.includes(needle));
    if (hit) return t(hit[1]);
  }

  const message = readMessage(e);
  const lower = message.toLowerCase();
  if (lower) {
    const hit = MESSAGE_RULES.find(([test]) => test(lower));
    if (hit) return t(hit[1]);
  }

  // A 401 body is a framework string ("Unauthenticated."), never a sentence
  // written for a voter — so `auth` prefers its own key over passing it through.
  if (e instanceof ApiError && e.code === "auth") return t("sessionExpired");

  if (message) return message;
  // A Firebase error we don't recognise is still an auth failure, not a mystery.
  return t(firebaseCode ? "authError" : "unknownError");
}
